<template>
  <div class="container">
    <h1 class="h1">Allow List</h1>
    <p>
      List of phone number prefixes, which are allowed for selected application.<br/>
      <i v-if="isAdmin">Admin Note: Blocked SMS have an entry in Outbox, except subscription is async over
        api-messaging-sms-entry.</i>
    </p>
    <div class="row margin-bottom-2">
      <div class="col-xs-4">
        <DevAppDropDownComponent role="sms" @devapp-changed="devAppChanged"/>
      </div>
    </div>
    <sdx-loading-spinner id="spinner-allowlist" size="large" sr-hint="Loading. Please wait."
                         class="spinner-dashboard"></sdx-loading-spinner>
    <div v-if="showAllowedPrefixesList" ref="allowedPrefixes">
      <div class="col-lg text-align-right margin-top-3">
        <sdx-dialog
            id="add-modal"
            label="Add Prefix(es)"
            type="closable-modal"
            class="clearfix"
        >
          <sdx-dialog-toggle>
            <sdx-button
                id="addAllowedPrefixButton"
                data-testid="addAllowedPrefixButton"
                label="Add"
                class="sdx-button-object"
            />
          </sdx-dialog-toggle>
          <sdx-dialog-content style="max-width: 688px">
            <div class="row">
              <div class="col margin-bottom-1">
                Following predefined prefix group can be used:
              </div>
              <div class="margin-bottom-3">
                <sdx-input-group type="checkbox">
                  <div class="row">
                    <div class="predefined-prefix" v-for="item in predefinedPrefixes" :key="item">
                      <p class="margin-bottom-1">
                        {{ item.name }}
                        <sdx-menu-flyout>
                          <sdx-menu-flyout-toggle sr-hint="Read more">
                            <sdx-icon icon-name="icon-information-circle"></sdx-icon>
                          </sdx-menu-flyout-toggle>
                          <sdx-menu-flyout-content class="infotag">
                            {{ item.description }}<br/>
                            <div v-if="item.countries">
                              <span v-for="prefix in item.countries.prefix" :key="Object.keys(prefix)[0]">
                                {{ Object.keys(prefix)[0] }} - {{ Object.values(prefix)[0] }}<br/>
                              </span>
                            </div>
                          </sdx-menu-flyout-content>
                        </sdx-menu-flyout>
                      </p>
                      <FlagComponent class="margin-bottom-1 flag-component" :country="item.name"/>
                      <sdx-input-item
                          id="'newPrefixByCheckbox-' + item.destRegionCode"
                          :checked="selectedPrefixes.includes(item.name) || allowedPrefixes.some(prefixObject => prefixObject.destRegionCode === item.name)"
                          :disabled="allowedPrefixes.some(prefixObject => prefixObject.destRegionCode === item.name)"
                          @change="togglePrefix(item.name)"
                      >
                      </sdx-input-item>
                    </div>
                  </div>
                </sdx-input-group>
              </div>
              <div class="col margin-bottom-1">
                If the predefined groups do not fit to your needs, you can manually add the prefix here, e.g. "41, 49".
              </div>
            </div>
            <div class="row">
              <div class="col margin-bottom-2">
                <sdx-input
                    id="newPrefixInput"
                    data-testid="newPrefixInput"
                    name="Prefix"
                    label="Prefix"
                    class="sdx-input"
                    placeholder=""
                    :value="typedPrefixes"
                    @input="typedPrefixes=$event.target.value;prefixValidation()"
                    :validation-message="(!validPrefix && typedPrefixes !== '') ? 'Prefix invalid': ''"
                >
                </sdx-input>
              </div>
            </div>
            <div class="margin-bottom-1">
              The allow list works by allowing every number starting with that prefix, so it is possible to allow a
              continent by adding the according prefix, e.g. for latin america the prefix would be "5".
              For more information see this <a
                href="https://de.wikipedia.org/wiki/Liste_der_L%C3%A4ndervorwahlen#Alphabetisch_sortiert"
                target="_blank">link</a>.
            </div>
            <sdx-button-group layout="responsive-fill">
              <sdx-button
                  id="cancelButton"
                  label="Cancel"
                  @click="closeModal('add-modal', 'addAllowedPrefixButton')"
                  theme="secondary"
              ></sdx-button>
              <sdx-button
                  id="addButtonModal"
                  data-testid="confirmAddAllowedPrefixButton"
                  label="Add"
                  @click="addPrefix()"
                  :disabled="selectedPrefixes.length === 0 && !validPrefix"
              ></sdx-button>
            </sdx-button-group>
          </sdx-dialog-content>
        </sdx-dialog>
      </div>
      <sdx-loading-spinner id="spinner-allowlist-content" ref="spinner-allowlist-content" size="large"
                           sr-hint="Loading. Please wait."></sdx-loading-spinner>
      <div id="allowlist-content" ref="allowlist-content">
        <div class="allowed-list-table">
          <div class="allowed-prefix-table table table--responsive table--highlight margin-v-2">
            <div class="table-wrapper">
              <table data-init="auto">
                <thead>
                <tr>
                  <th class="table-header small-column">Region or Country</th>
                  <th class="table-header">Prefixes</th>
                  <th class="table-header text-align-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in allowedPrefixes" :key="item">
                  <td class="table-cell text-align-center">
                    <FlagComponent :country="item['destRegionCode']"/>
                    {{ item.destRegionCode }}
                  </td>
                  <td>
                    <div v-for="prefix in item.prefix" :key="Object.keys(prefix)[0]">
                      {{ Object.keys(prefix)[0] }} - {{ Object.values(prefix)[0] }}
                    </div>
                  </td>
                  <td style="padding-top:5px;">
                    <div class="toolbar text-align-center" id="toolbar01">
                      <div class="toolbar__item item--show" style="padding-left: 5px;">
                        <sdx-dialog
                            v-bind:id="'remove-modal' + item.destRegionCode"
                            label="Remove Prefix?"
                            icon-name="icon-bin"
                        >
                          <sdx-dialog-toggle>
                            <button aria-label="Remove"
                                    id="removeActionButton">
                              <i class="icon icon-bin" title="Remove"></i>
                            </button>
                          </sdx-dialog-toggle>
                          <sdx-dialog-content>
                            <p>Do you really want to remove the prefix <strong>{{ item.destRegionCode }}</strong> from
                              the allow list?</p>
                            <sdx-button-group layout="responsive-fill">
                              <sdx-button
                                  id="noButton"
                                  label="No"
                                  @click="closeModal('remove-modal' + item.destRegionCode)"
                                  theme="secondary"></sdx-button>
                              <sdx-button
                                  id="yesButton"
                                  label="Yes"
                                  @click="deleteAllowedPrefix(item)"></sdx-button>
                            </sdx-button-group>
                          </sdx-dialog-content>
                        </sdx-dialog>
                      </div>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div v-if="allowedPrefixes.length === 0" class="no-items-found">
          <p><i>No allowed prefix is defined. Default is only Switzerland +41</i></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DevAppDropDownComponent from "@/components/DevAppDropDownComponent.vue";
import loadingSpinnerService from '@/services/loading-spinner.service';
import allowedPrefixesService from '../service'
import FlagComponent from "@/components/FlagComponent";
import {validatePrefix} from "@/util/utility";
import {showAlert, showSuccess} from "@/services/notification.service";
import {logger} from "@/services/logger.service";
import userService from "@/services/user.service";

export default {
  components: {
    DevAppDropDownComponent,
    FlagComponent
  },
  data: function () {
    return {
      webId: undefined,
      cookieName: undefined,
      typedPrefixes: '',
      selectedPrefixes: [],
      allowedPrefixes: [],
      showAllowedPrefixesList: false,
      validPrefix: false,
      predefinedPrefixes: [
        {name: "CH", description: "Includes only Switzerland"},
        {name: "CH AND NEIGHBOURS", description: "Includes Switzerland and all the neighbouring countries:"},
        {name: "EU", description: "Includes every country in the european union"}
      ]
    }
  },
  async mounted() {
    loadingSpinnerService.hide("allowlist");
    for (var prefix of this.predefinedPrefixes) {
      var response = await allowedPrefixesService.getPrefixesByDestinationGroup("notused", encodeURIComponent(prefix.name));
      prefix.countries = response.data;
    }
    logger.debug(JSON.stringify(this.predefinedPrefixes))
  },
  computed: {
    isAdmin() {
      return userService.isAtLeastAdminWithoutFmg();
    }
  },
  methods: {
    async devAppChanged(webId) {
      loadingSpinnerService.show("allowlist")
      this.webId = webId;
      this.cookieName = "ALLOWED-PREFIXES-" + this.webId;
      await this.loadAllowedPrefixes();
      loadingSpinnerService.hide("allowlist")
    },
    async loadAllowedPrefixes() {
      loadingSpinnerService.show("allowlist-content")
      if (this.webId === undefined || this.webId === null || this.webId === '') {
        this.showAllowedPrefixesList = false
      } else {
        try {
          const allowedPrefixesCookie = document.cookie.match(this.cookieName + '\\s*=\\s*([^;]+)')?.pop();

          if (allowedPrefixesCookie) {
            this.allowedPrefixes = JSON.parse(allowedPrefixesCookie);
          } else {
            this.allowedPrefixes = (await allowedPrefixesService.getAllAllowedPrefixes(this.webId)).data;
            document.cookie = this.cookieName + "=" + JSON.stringify(this.allowedPrefixes) + "; max-age=180; path=/";
          }
          this.showAllowedPrefixesList = true;
          logger.debug(JSON.stringify(this.allowedPrefixes))
        } catch (error) {
          logger.error(error)
          this.allowedPrefixes = [];
          showAlert(error.message)
        }
        this.isLoading = false;
      }
      loadingSpinnerService.hide("allowlist-content")
    },
    addPrefix: async function () {
      try {
        const typedPrefixesList = this.typedPrefixes
            .split(',')
            .map(prefix => prefix.trim())
            .filter(prefix => prefix !== '');

        let mappedPrefixes = [];

        for (const prefixObj of this.allowedPrefixes) {
          mappedPrefixes.push(this.getPrefixOfObject(prefixObj));
        }
        const allPrefixes = [...new Set([...this.selectedPrefixes, ...typedPrefixesList, ...mappedPrefixes])];
        let response = await allowedPrefixesService.updatePrefixes(this.webId, allPrefixes);
        this.allowedPrefixes = response.data;
        document.cookie = this.cookieName + "=" + JSON.stringify(this.allowedPrefixes) + "; max-age=180; path=/";
        this.selectedPrefixes = [];
        this.closeModal('add-modal', 'addAllowedPrefixButton');
        showSuccess('Prefix is added successfully!');
      } catch (exception) {
        this.closeModal('add-modal', 'addAllowedPrefixButton');
        showAlert("Could not add the prefix, it already exists!");
      }
    }
    ,
    async deleteAllowedPrefix(prefixObjToRemove) {
      try {
        let mappedPrefixes = [];
        for (const prefixObj of this.allowedPrefixes) {
          mappedPrefixes.push(this.getPrefixOfObject(prefixObj));
        }
        let prefixToRemove = this.getPrefixOfObject(prefixObjToRemove);
        mappedPrefixes = mappedPrefixes.filter(prefix => prefix !== prefixToRemove);

        let response = await allowedPrefixesService.updatePrefixes(this.webId, mappedPrefixes);
        this.allowedPrefixes = response.data;
        document.cookie = this.cookieName + "=" + JSON.stringify(this.allowedPrefixes) + "; max-age=180; path=/";
        this.closeModal('remove-modal' + prefixObjToRemove.destRegionCode);
        showSuccess('Prefix is deleted successfully!')
      } catch (exception) {
        this.closeModal('remove-modal' + prefixObjToRemove.destRegionCode);
        showAlert("Could not delete the prefix!")
      }
    },
    prefixValidation() {
      return this.validPrefix = validatePrefix(this.typedPrefixes);
    },
    closeModal(elementId, toggelButtonId) {
      this.typedPrefixes = '';
      document.getElementById(elementId).close();
      //Alternative solution to close the modal in case of issues with the above line
      if(toggelButtonId != undefined) {
        document.getElementById(toggelButtonId).click()
        logger.debug("Closed modal with alternative solution")
      }
    },
    togglePrefix(prefix) {
      const index = this.selectedPrefixes.indexOf(prefix);
      if (index > -1) {
        this.selectedPrefixes.splice(index, 1);
      } else {
        this.selectedPrefixes.push(prefix);
      }
    }
    ,
    getPrefixOfObject(prefixObj) {
      const predefinedMatch = this.predefinedPrefixes.find(predefined => predefined.name === prefixObj.destRegionCode);
      if (predefinedMatch) {
        return predefinedMatch.name;
      } else {
        return Object.keys(prefixObj.prefix[0])[0];
      }
    }
  }
}
</script>

<style>
.allowed-prefix-table {
  padding-top: 10px;
}

.allowed-prefix-table .table-header {
  padding-top: 20px;
  padding-bottom: 17px;
}

.allowed-prefix-table .table-cell {
  padding-top: 5px;
  padding-bottom: 2px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.allowed-prefix-table.table tbody td {
  padding-top: 5px;
  padding-bottom: 3px;
}

.infotag {
  width: 300px;
}

.predefined-prefix {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 200px;
  padding-top: 20px;
}

.flag-component {
  width: 50px;
  height: 50px;
  border-radius: 50px;
}

.allowed-prefix-table.table tbody td {
  padding-top: 17px;
  vertical-align: top;
}

.small-column {
  width: 20%;
}
</style>